import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  HostListener,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  NgZone,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TicketsService } from '../../../../../core/services/tickets.service';
import { LoadingService } from '../../../../../shared/components/loader/loading.service';
import { Loading } from '../../../../../shared/components/loader/loading/loading';
import { PaymentService } from '../../../../../core/services/payment.service';
import { Router } from '@angular/router';
import { CardService } from '../../../../../core/services/card.service';
import { AccountService } from '../../../../../core/services/account.service';
import { firstValueFrom } from 'rxjs';
import { UploadImageService } from '../../../../../core/services/uploadimage.service';
import { AlertService } from '../../../../../shared/components/alert/alert';
import { ResultService } from '../../../../../core/services/result.service';
import { SocketService } from '../../../../../core/services/socket.service';
import { TranslatePipe } from '../../../../../shared/pipes/translate.pipe';
import { TranslationService } from '../../../../../core/services/translation.service';

interface Ticket {
  id: string;
  alphabet: string;
  number: string;
  code: string;
}

@Component({
  selector: 'app-ticket-list',
  imports: [CommonModule, FormsModule, Loading, TranslatePipe],
  templateUrl: './ticket-list.html',
  styleUrls: ['./ticket-list.css'],
})
export class TicketList implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  tickets: Ticket[] = [];
  ticketCounts: number[] = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 100];
  selectedCount: number | null = null;
  searchNumber: string = '';

  alphabets: string[] = [];
  selectedAlphabet: string = '';
  dropdownOpen = false;
  aboutUsOpen = false;
  selectedTickets: Ticket[] = [];

  currentPage: number = 1;
  ticketsPerPage: number = 8;
  totalPages: number = 1;

  showPaymentModal = false;
  isQRFullScreen = false;

  imageFile: File | null = null;
  imagePreview: string | null = null;
  screenshotBase64: string | null = null;

  paymentName: string = '';
  paymentAddress: string = '';
  paymentPhone: string = '';
  allCards: any[] = [];
  selectedCard: any = null;
  invoiceNo: string | null = null;
  showInvoiceAlert = false;

  result: any = {};
  allAccounts: any[] = [];
  randomOneAccount: any | null = null;
  paymentSubmitted = false;
  loadingResult = false;
  loadingPayment = false;
  imageBaseUrl = 'https://ts-space.sgp1.digitaloceanspaces.com/uploads/lottery/admin/image/';

  constructor(
    private ticketsService: TicketsService,
    public loadingService: LoadingService,
    private cdr: ChangeDetectorRef,
    private paymentService: PaymentService,
    private resultService: ResultService,
    private cardService: CardService,
    private accountService: AccountService,
    private uploadImageService: UploadImageService,
    private alertService: AlertService,
    private router: Router,
    private socketService: SocketService,
    private translationService: TranslationService,
  ) {}

  ngOnInit(): void {
    this.alphabets = ['ALL', 'A-', 'B-', 'C-'];
    this.updateTicketsPerPage();
    this.loadTickets();
    this.loadResult();
    this.fetchCards();
    this.fetchAccounts();
  }

  currentLang() {
    return this.translationService.userLang();
  }

  setLanguage(lang: 'en' | 'mm') {
    this.translationService.setUserLanguage(lang);
  }

  loadResult() {
    this.loadingResult = true;
    const payload = {};
    this.resultService.getCurrentMonthResult(payload).subscribe({
      next: (res: any) => {
        if (res.returncode === '300' && res.data) {
          this.result = res.data;
        } else {
          this.result = {};
        }
        this.loadingResult = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to fetch result:', err);
        this.loadingResult = false;
        this.cdr.detectChanges();
      },
    });
  }

  loadTickets(alphabet: string = '', number: string = '') {
    this.loadingService.show();

    const payload = {
      page: this.currentPage.toString(),
      limit: this.ticketsPerPage.toString(),
      alphabet,
      number,
    };

    this.ticketsService.getCurrentMonthTickets(payload).subscribe({
      next: (res: any) => {
        if (res.returncode === '300' && res.data) {
          this.tickets = res.data.data.map((t: any) => ({
            ...t,
            code: t.alphabet + t.number,
          }));

          const meta = res.data.meta;
          if (meta) {
            this.currentPage = meta.currentPage || this.currentPage;
            this.ticketsPerPage = meta.perPage || this.ticketsPerPage;
            this.totalPages = meta.totalPages || 1;
          }
        } else {
          this.tickets = [];
        }

        this.loadingService.hide();
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to fetch tickets:', err);
        this.loadingService.hide();
        this.cdr.detectChanges();
      },
    });
  }

  fetchAccounts() {
    this.accountService.getAllAcc().subscribe((res) => {
      this.allAccounts = res.data.data;
    });
  }

  @HostListener('window:resize')
  onResize() {
    this.updateTicketsPerPage();
  }

  updateTicketsPerPage() {
    const isMobile = window.innerWidth <= 768;
    const newLimit = isMobile ? 8 : 28;

    if (this.ticketsPerPage !== newLimit) {
      this.ticketsPerPage = newLimit;
      this.currentPage = 1;
      this.loadTickets(
        this.selectedAlphabet === 'ALL' ? '' : this.selectedAlphabet,
        this.searchNumber,
      );
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const dropdownElement = document.getElementById('alphabet-dropdown');
    const triggerElement = document.getElementById('alphabet-trigger');

    if (
      this.dropdownOpen &&
      dropdownElement &&
      triggerElement &&
      !dropdownElement.contains(target) &&
      !triggerElement.contains(target)
    ) {
      this.dropdownOpen = false;
    }
  }

  searchTickets() {
    this.currentPage = 1;

    const alphabet = this.selectedAlphabet === 'ALL' ? '' : this.selectedAlphabet;

    this.loadTickets(alphabet, this.searchNumber);
  }

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }

  toggleAboutUs() {
    this.aboutUsOpen = !this.aboutUsOpen;
  }

  selectAlphabet(letter: string) {
    this.selectedAlphabet = letter;
    this.dropdownOpen = false;

    if (letter === 'ALL') {
      this.loadTickets('', this.searchNumber);
    } else {
      this.loadTickets(letter, this.searchNumber);
    }
  }

  toggleTicket(ticket: Ticket) {
    const index = this.selectedTickets.findIndex((t) => t.id === ticket.id);

    if (index > -1) {
      this.selectedTickets.splice(index, 1);
    } else {
      this.selectedTickets.push(ticket);
    }

    this.cdr.detectChanges();
  }

  success() {
    this.router.navigate(['/purchase']);
  }

  onCountChange(value: string) {
    const count = Number(value);
    this.selectedCount = count;

    if (count === 0) {
      this.selectedTickets = [];
    } else {
      this.ticketsPerPage = 100;
      this.currentPage = 1;
      this.loadingService.show();

      const alphabet = this.selectedAlphabet === 'ALL' ? '' : this.selectedAlphabet;
      const payload = {
        page: '1',
        limit: count,
        alphabet,
        number: this.searchNumber,
      };

      this.ticketsService.getCurrentMonthTickets(payload).subscribe({
        next: (res: any) => {
          if (res.returncode === '300' && res.data) {
            this.tickets = res.data.data.map((t: any) => ({
              ...t,
              code: t.alphabet + t.number,
            }));

            const meta = res.data.meta;
            if (meta) {
              this.currentPage = meta.currentPage || this.currentPage;
              this.ticketsPerPage = meta.perPage || this.ticketsPerPage;
              this.totalPages = meta.totalPages || 1;
            }

            this.selectedTickets = [...this.tickets];

            this.openPaymentModal();
          }
          this.loadingService.hide();
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Failed to fetch tickets:', err);
          this.loadingService.hide();
          this.cdr.detectChanges();
        },
      });
    }
  }

  getRandomTickets(count: number): string[] {
    const shuffled = [...this.tickets].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count).map((t: any) => t.id);
  }

  isSelected(ticketId: string): boolean {
    return this.selectedTickets.some((t) => t.id === ticketId);
  }

  get visiblePages(): (number | string)[] {
    const total = this.totalPages;
    const current = this.currentPage;
    const delta = 1;

    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];
    pages.push(1);

    if (current > delta + 3) {
      pages.push('...');
    }

    const start = Math.max(2, current - delta);
    const end = Math.min(total - 1, current + delta);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (current < total - delta - 2) {
      pages.push('...');
    }

    pages.push(total);
    return pages;
  }

  get displayTickets(): Ticket[] {
    return this.tickets;
  }

  goToPage(page: number) {
    if (page < 1 || page > this.totalPages) return;

    this.currentPage = page;

    const alphabet = this.selectedAlphabet === 'ALL' ? '' : this.selectedAlphabet;

    this.loadTickets(alphabet, this.searchNumber);
  }

  onLimitChange() {
    this.currentPage = 1;

    const alphabet = this.selectedAlphabet === 'ALL' ? '' : this.selectedAlphabet;

    this.loadTickets(alphabet, this.searchNumber);
  }

  openPaymentModal() {
    this.showPaymentModal = true;
    this.imagePreview = '';
    this.selectedCard = '';
    this.randomOneAccount = null;
    this.paymentSubmitted = false;
    this.reserveTickets();
    const ticketSub = this.socketService
      .onTicketExpired(this.selectedTickets[0].id)
      .subscribe((data) => {
        console.log('Ticket expired:', data);
        if (data.status === '01') {
          this.closePaymentModal();
          this.alertService.error('Session Timeout');
          this.selectedTickets = [];
          this.updateTicketsPerPage();
          this.loadTickets();
          this.cdr.detectChanges();
        }
      });
  }

  reserveTickets() {
    const ticketIds = this.selectedTickets.map((ticket) => ticket.id);
    this.ticketsService.reserveTickets(ticketIds).subscribe({
      next: (res: any) => {
        if (res.returncode === '200') {
          this.alertService.error(res.message || 'Some tickets already reserved');
          this.closePaymentModal();
          this.selectedTickets = [];
          this.loadTickets();
          this.cdr.detectChanges();
        }
      },
      error: (err) => {
        console.error('Failed to reserve tickets:', err);
      },
    });
  }

  unreserveTickets() {
    const ticketIds = this.selectedTickets.map((ticket) => ticket.id);
    this.ticketsService.unreserveTickets(ticketIds).subscribe((res) => {});
  }

  triggerFileInput() {
    this.fileInput.nativeElement.value = '';
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.imagePreview = e.target.result;
        this.imageFile = file;
        this.screenshotBase64 = e.target.result as string;
        this.cdr.markForCheck();
      };
      reader.readAsDataURL(file);
    }
  }

  fetchCards() {
    this.cardService.getAllCardsUser().subscribe((res) => {
      this.allCards = res.data.data;
    });
  }

  closePaymentModal() {
    this.showPaymentModal = false;
    this.isQRFullScreen = false;
    this.resetPaymentForm();
  }

  openFullQR() {
    this.isQRFullScreen = true;
  }

  closeFullQR() {
    this.isQRFullScreen = false;
  }

  clsoeButtonClick() {
    this.closePaymentModal();
    this.unreserveTickets();
  }

  resetPaymentForm() {
    this.paymentName = '';
    this.paymentAddress = '';
    this.paymentPhone = '';
    this.selectedCard = null;
    this.imageFile = null;
    this.imagePreview = null;
    this.screenshotBase64 = null;
    this.paymentSubmitted = false;
    this.randomOneAccount = null;
    this.loadingPayment = false;
  }

  async confirmPayment() {
    this.paymentSubmitted = true;
    if (!this.paymentName) {
      this.alertService.error('Please fill name');
      return;
    }
    if (!this.paymentAddress) {
      this.alertService.error('Please fill address');
      return;
    }
    if (!this.paymentPhone) {
      this.alertService.error('Please fill phone');
      return;
    }
    if (!this.selectedCard) {
      this.alertService.error('Please choose payment card');
      return;
    }
    if (!this.imagePreview) {
      this.alertService.error('Please upload screenshot');
      return;
    }

    this.loadingPayment = true;
    const res = await firstValueFrom(this.uploadImageService.uploadImageForUser(this.imageFile));
    if (res.returncode == '300') {
      const screenshot = res.data.url;
      const payload = {
        ticket: this.selectedTickets.map((t) => t.id),
        name: this.paymentName,
        address: this.paymentAddress,
        phone: this.paymentPhone,
        screenshot,
      };

      this.paymentService.createPayment(payload).subscribe({
        next: (res: any) => {
          if (res.returncode === '300') {
            this.invoiceNo = res.data.invoiceno;
            this.alertService.success('Payment Created Successfully');
            this.showInvoice(res.data.invoiceno);
            this.closePaymentModal();
            this.selectedTickets = [];
            this.updateTicketsPerPage();
            this.loadTickets();
            this.cdr.detectChanges();
          } else {
            this.loadingPayment = false;
            this.alertService.error(res.message || 'Payment failed');
            this.uploadImageService.deleteImage(screenshot).subscribe({
              next: () => console.log('Orphaned image deleted'),
              error: (e) => console.error('Failed to delete image', e),
            });
            this.cdr.detectChanges();
          }
        },
        error: (err) => {
          console.error('Error', err);
          this.loadingPayment = false;
          this.uploadImageService.deleteImage(screenshot).subscribe({
            next: () => console.log('Orphaned image deleted successfully'),
            error: (delErr) => console.error('Failed to delete orphaned image:', delErr),
          });
          alert('Payment failed. Please try again.');
          this.cdr.detectChanges();
        },
      });
    } else {
      this.loadingPayment = false;
      this.alertService.error(res.message || `Failed to payment`);
      this.cdr.detectChanges();
    }
  }

  showInvoice(invoice: string) {
    this.invoiceNo = invoice;
    this.showInvoiceAlert = true;
  }

  copyInvoiceAlert() {
    if (!this.invoiceNo) return;
    navigator.clipboard.writeText(this.invoiceNo).then(() => {
      console.log('Invoice copied:', this.invoiceNo);
    });
  }

  closeInvoiceAlert() {
    this.showInvoiceAlert = false;
    this.invoiceNo = null;
  }

  onCardClick(card: any) {
    this.selectedCard = card;
    this.callCardUserApi(card.id);
  }

  callCardUserApi(cardId: string) {
    const selectedCardData = this.allAccounts.filter((item: any) => item.cardid === cardId);
    this.randomOneAccount = selectedCardData[Math.floor(Math.random() * selectedCardData.length)];

    if (this.randomOneAccount && this.randomOneAccount.t1) {
      if (!this.randomOneAccount.t1.startsWith('http')) {
        this.randomOneAccount.t1 = this.imageBaseUrl + this.randomOneAccount.t1;
      }
    }

    this.cdr.detectChanges();
  }
}
